import { useEffect, useRef, useState } from "react";
import { Editor as TMEditor } from "@tinymce/tinymce-react";
import { TextInput } from "flowbite-react";
import { FlatButton } from "../buttons";
import { Attachments, useAttachmentHandler } from "../attachments";
import { useDispatch, useSelector } from "react-redux";
import { SAVENOTE_REQ } from "../../actions";
import { SAVENOTE_RESPONSE } from "../../constants/action_types";
import { UserAvatar } from "../user_avatar";
import Avatar from "../../assets/images/avatar.svg";
import { axiosGet } from "../../util/axios";
import { HTTP_SUCCESS_STATUSES } from "../../constants/http_constants";

const isValidURL = (string) => {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
};

export function Note({
  company_uid,
  newNote,
  attachment_popup,
  note,
  size = "xs",
  toolbarAlign = "start",
  onEditCancel,
  onEditSave,
}) {
  const sizeClass = {
    xs: "[&_.mce-content-body]:h-[90px] h-[135px]",
    sm: "[&_.mce-content-body]:h-[120px] h-[165px]",
  };
  const dispatch = useDispatch();
  const editorRef = useRef();
  const mentionsRef = useRef([]);

  const notesUpdate = useSelector((state) => state?.company?.update_response);

  const [title, setTitle] = useState(note?.note_title, "");
  const attacmentHandler = useAttachmentHandler({});

  useEffect(() => {
    !newNote && attacmentHandler.reset();
    !newNote && attacmentHandler.addFiles(note?.attachments || []);
  }, []);

  useEffect(() => {
    if (notesUpdate.type === SAVENOTE_RESPONSE) {
      attacmentHandler.reset();
      editorRef.current.resetContent();
      setTitle("");
    }
  }, [notesUpdate]);

  const handleAttachment = (files) => {
    attacmentHandler.addFiles(files);
    editorRef.current?.notificationManager.open({
      text: `Attached ${files.length} file${files.length > 1 ? "s" : ""}`,
      type: "success",
      timeout: 1000,
    });
  };

  const saveNote = () => {
    if (!title) {
      editorRef.current.focus();
      editorRef.current?.notificationManager.open({
        text: "Please enter a title",
        type: "warn",
        timeout: 3000,
      });
    } else {
      const content = editorRef.current.getContent();
      dispatch(
        SAVENOTE_REQ({
          note_title: title,
          company_uid: company_uid,
          note_content: editorRef.current.getContent(),
          attachments: attacmentHandler.attachments,
          mentions:
            mentionsRef.current?.filter((mention) =>
              content.includes(mention)
            ) || [],
        })
      );
    }
  };
  return (
    <div className=" w-full">
      <div className="inline-flex  w-full gap-x-4 border-2 rounded-lg p-2 flex-wrap">
        <UserAvatar className="flex-shrink-0" />
        <div class=" min-h-[125px] flex-1">
          <TextInput
            id="title"
            placeholder="Title"
            className="[&_input]:border-none [&_input:focus]:border-none [&_input:focus]:ring-0
         [&_input]:bg-transparent"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
          <div id="editor-container" className={`relative ${sizeClass[size]} `}>
            <div
              id="editor-wrapper"
              onPaste={(event) => {
                if (event.clipboardData.files.length > 0) {
                  handleAttachment(event.clipboardData.files, true);
                }
              }}
              className={`relative text-sm px-2.5 pt-2  border-t-2 overflow-auto overflow-x-hidden scrollbar [&_.mce-content-body]:outline-none prose`}
            >
              <div id={note?.note_uid || "newNote"} />
              <TMEditor
                id={note?.note_uid || "newNote"}
                apiKey="1vzq8pkbususfgf4m53lfdvxb0qczsqdayq768ikcj37r2cv"
                inline
                init={{
                  selector: `#${note?.note_uid || "newNote"}`,
                  placeholder: "Add a note...",
                  non_editable_class: "no-edit",
                  height: "300px",
                  width: "75%",
                  menubar: false,
                  statusbar: false,
                  plugins: "lists link autolink paste",
                  link_assume_external_targets: true,
                  paste_as_text: true,
                  toolbar_location: "auto",
                  relative_urls: false,
                  document_base_url: process.env.REACT_APP_PUBLIC_URL,
                  remove_script_host: false,
                  toolbar_mode: "scrolling",
                  fixed_toolbar_container: `#TMToolbar-${
                    note?.note_uid || "newNote"
                  }`,
                  toolbar:
                    "undo redo bold italic underline alignLeft alignCenter alignJustify alignRight link numlist bullist forecolor backcolor saveBtn",
                  paste_data_images: false,
                  setup: (editor) => {
                    editorRef.current = editor;
                    editor.ui.registry.addAutocompleter("coworker_mention", {
                      trigger: "@",
                      fetch: async (pattern) => {
                        let mentions = [];
                        let response = await axiosGet("/coworkers", {
                          params: { "filter.keyword": pattern },
                        });
                        if (HTTP_SUCCESS_STATUSES.includes(response?.status)) {
                          mentions = response.data?.data?.length
                            ? response.data.data.map((coworker) => ({
                                type: "cardmenuitem",
                                value: coworker,
                                items: [
                                  {
                                    type: "cardcontainer",
                                    items: [
                                      {
                                        type: "cardimage",
                                        src: coworker.profile_pic
                                          ? !isValidURL(coworker.profile_pic)
                                            ? `${process.env.REACT_APP_ATTACHMENT_URL}${coworker.profile_pic}`
                                            : coworker.profile_pic
                                          : Avatar,
                                        alt: "profile_picture",
                                        classes: [
                                          "!h-8",
                                          "!w-8",
                                          "!mr-3",
                                          "rounded-full",
                                          "inline-block",
                                          "object-contain",
                                          "!ring-2",
                                          "!ring-gray-200",
                                          "!bg-white",
                                        ],
                                      },
                                      {
                                        type: "cardtext",
                                        text: coworker.fullName,
                                        classes: [
                                          "!inline-block",
                                          "!text-md",
                                          "!text-gray-700",
                                          "!whitespace-nowrap",
                                        ],
                                      },
                                    ],
                                  },
                                ],
                              }))
                            : [];
                          return mentions;
                        }
                      },
                      onAction: (autocompleteApi, rng, value) => {
                        let content = `<a href='${
                          process.env.REACT_APP_PUBLIC_URL
                        }coworkers/${value?.coworker_uid || ""}/details'>@${
                          value?.fullName
                        }</a>`;
                        editor.selection.setRng(rng);
                        console.log(content);
                        editor.insertContent(content);
                        autocompleteApi.hide();
                        if (
                          !mentionsRef.current?.includes(value?.coworker_uid)
                        ) {
                          mentionsRef.current = [
                            ...(mentionsRef.current || []),
                            value?.coworker_uid,
                          ];
                        }
                      },
                    });
                    setTimeout(() => {
                      if (!newNote) {
                        const regex = /\/coworkers\/([0-9a-fA-F-]+)\/details/g;
                        const mentions = new Set();

                        let match;
                        while (
                          (match = regex.exec(note.note_content)) !== null
                        ) {
                          if (match[1]) {
                            mentions.add(match[1]);
                          }
                        }

                        mentionsRef.current = [...mentions];
                        editor.setContent(note.note_content);
                      }
                      editor.notificationManager.close();
                    }, 200);
                  },
                }}
              />
            </div>
            <div
              id={`TMToolbar-${note?.note_uid || "newNote"}`}
              className={`[&_*]:!border-0 [&_.tox-editor-header]:!bg-transparent [&_.tox-toolbar]:!bg-transparent [&_.tox-tbtn]:!h-auto [&_.tox-tbtn]:!m-0 w-full flex items-center justify-${toolbarAlign}`}
            />
          </div>
        </div>
      </div>
      {!attachment_popup && (
        <Attachments attachmentHandler={attacmentHandler} />
      )}
      <div
        id="editor-actions"
        className="w-full flex items-center mt-2 px-2 !border-solid border-t-2 pt-2"
      >
        {attachment_popup && (
          <Attachments popup attachmentHandler={attacmentHandler} />
        )}
        <div className="ml-auto flex gap-x-4">
          {!newNote && (
            <FlatButton
              appearance="secondary"
              className="w-[100px]"
              onClick={() => {
                onEditCancel && onEditCancel();
              }}
            >
              Cancel
            </FlatButton>
          )}
          <FlatButton
            className="w-[100px]"
            onClick={() => {
              const content = editorRef.current.getContent();
              !newNote
                ? onEditSave &&
                  onEditSave({
                    ...note,
                    note_title: title,
                    company_uid: company_uid,
                    note_content: editorRef.current.getContent(),
                    attachments: attacmentHandler.attachments,
                    mentions: mentionsRef.current?.filter((mention) =>
                      content.includes(mention)
                    ),
                  })
                : saveNote();
            }}
          >
            Save
          </FlatButton>
        </div>
      </div>
    </div>
  );
}
